/**
 * Resilience against third-party DOM mutation, primarily Chrome's built-in
 * page translation. Translate moves React-managed text nodes into <font>
 * wrappers; React's commit phase then calls removeChild/insertBefore with
 * stale parent references and throws DOMException NotFoundError, which
 * unmounts the whole tree (permanent white screen without a boundary).
 *
 * Seen live 2026-07-14: a brand-new ko-KR user crashed opening /issues/SP-1
 * (PostHog issue 019f5f6c-bcc1-7493-b2a9-d3edadffce79). Standard mitigation
 * from facebook/react#11538: tolerate the moved nodes instead of throwing.
 *
 * Kept free of logger/analytics imports (injected callback instead) so it
 * stays testable under ts-jest - same pattern as lib/onboardingFinish.ts.
 */

type GuardedOperation = 'removeChild' | 'insertBefore';

export type GuardTelemetry = (
  operation: GuardedOperation,
  pageTranslated: boolean,
) => void;

/** Chrome stamps these classes on <html> while a page is translated. */
export function isPageTranslated(): boolean {
  const cls = document.documentElement.classList;
  return cls.contains('translated-ltr') || cls.contains('translated-rtl');
}

const GUARD_FLAG = '__oqaTranslateDomGuard';

export function installTranslateDomGuards(onGuardTriggered?: GuardTelemetry): void {
  if (typeof Node !== 'function' || !Node.prototype) return;

  // Idempotence keyed on the installed function itself (not module state) so
  // tests that restore the prototype can reinstall cleanly.
  const current = Node.prototype.removeChild as unknown as Record<string, unknown>;
  if (current[GUARD_FLAG]) return;

  // First repair per pageload is signal enough; a translated page repairs on
  // nearly every render and would otherwise flood telemetry.
  let reported = false;
  const report = (operation: GuardedOperation): void => {
    if (reported) return;
    reported = true;
    try {
      onGuardTriggered?.(operation, isPageTranslated());
    } catch {
      // Telemetry must never break the repair path.
    }
  };

  const originalRemoveChild = Node.prototype.removeChild;
  function guardedRemoveChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      report('removeChild');
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  }

  const originalInsertBefore = Node.prototype.insertBefore;
  function guardedInsertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      report('insertBefore');
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  }

  Node.prototype.removeChild = guardedRemoveChild;
  Node.prototype.insertBefore = guardedInsertBefore;
  (guardedRemoveChild as unknown as Record<string, unknown>)[GUARD_FLAG] = true;
}
